import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Radio, Check, Loader2, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } } };
const inView = { initial: "hidden", whileInView: "show", viewport: { once: true, amount: 0.2 } };

const FEATURES = {
  connect: ["Unlimited member submitters", "Up to 5 admin users", "Branded member portal", "Requests, approvals & tracking", "Automated notifications", "Request history & archive"],
  connectplus: ["Everything in CONNECT", "Up to 15 admin users", "Production dashboard", "Team assignments & scheduling", "Communications calendar", "Committee-based routing"],
  pro: ["Everything in CONNECT+", "Up to 30 admin users", "Publication / issue builder", "Email-platform export", "Advanced & turnaround analytics", "Custom workflow configuration"],
  network: ["Everything in PRO", "Multiple local-unit environments", "Central administration", "Organization-wide reporting", "Central templates & standards", "Dedicated support"],
};
const ORDER = ["connect", "connectplus", "pro"];

const emptySignup = { org_name: "", contact_name: "", contact_email: "", contact_phone: "", notes: "" };

export default function Pricing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [period, setPeriod] = useState("monthly");
  const [dialogPlan, setDialogPlan] = useState(null);
  const [form, setForm] = useState(emptySignup);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/billing/plans").then((r) => setPlans(r.data.plans)).catch(() => {}); }, []);

  const planMap = plans.reduce((a, p) => ({ ...a, [p.id]: p }), {});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openSignup = (planId) => { setForm(emptySignup); setDialogPlan(planId); };

  const startCheckout = async () => {
    if (!form.org_name || !form.contact_name || !form.contact_email) {
      return toast.error("Organization, name and email are required");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan: dialogPlan, period, origin_url: window.location.origin, ...form,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  const priceOf = (p) => period === "monthly" ? p.monthly : p.yearly;
  const dp = dialogPlan ? planMap[dialogPlan] : null;
  const inputCls = "mt-1 w-full rounded-lg border border-[#DDE2E7] px-3 py-2 text-sm text-[#252A31] focus:border-[#5F7FA3] focus:outline-none focus:ring-1 focus:ring-[#5F7FA3]";

  return (
    <div className="min-h-screen bg-[#F7F8FA] text-[#252A31]" data-testid="pricing-page">
      <nav className="sticky top-0 z-20 flex items-center justify-between border-b border-[#DDE2E7] bg-[#F7F8FA]/85 px-6 py-4 backdrop-blur-xl lg:px-12">
        <button onClick={() => navigate("/home")} className="flex items-center gap-2.5" data-testid="pricing-logo">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#243447] text-white"><Radio className="h-5 w-5" /></div>
          <span className="text-lg font-bold tracking-tight text-[#243447]">CommConnect</span>
        </button>
        <button onClick={() => navigate("/home")} data-testid="pricing-back"
          className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-[#243447] transition hover:bg-[#E8EEF4]">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
      </nav>

      <motion.section variants={stagger} initial="hidden" animate="show" className="px-6 pt-16 pb-8 text-center lg:px-12">
        <motion.span variants={fadeUp} className="inline-flex items-center gap-2 rounded-full border border-[#DDE2E7] bg-[#E8EEF4] px-4 py-1.5 text-xs font-medium text-[#5F7FA3]">
          <Sparkles className="h-3.5 w-3.5" /> One subscription. Your entire organization.
        </motion.span>
        <motion.h1 variants={fadeUp} className="mx-auto mt-6 max-w-3xl text-4xl font-bold tracking-tight text-[#243447] sm:text-5xl">Simple, organization-based pricing</motion.h1>
        <motion.p variants={fadeUp} className="mx-auto mt-4 max-w-2xl text-lg text-[#565E6B]">Unlimited member submitters on every plan. Pricing scales with workflow depth, publishing, and oversight — not headcount.</motion.p>

        <motion.div variants={fadeUp} className="mt-8 inline-flex items-center rounded-full border border-[#DDE2E7] bg-white p-1" data-testid="billing-toggle">
          <button onClick={() => setPeriod("monthly")} data-testid="toggle-monthly"
            className={`rounded-full px-5 py-2 text-sm font-semibold transition ${period === "monthly" ? "bg-[#243447] text-white" : "text-[#565E6B]"}`}>Monthly</button>
          <button onClick={() => setPeriod("yearly")} data-testid="toggle-yearly"
            className={`rounded-full px-5 py-2 text-sm font-semibold transition ${period === "yearly" ? "bg-[#243447] text-white" : "text-[#565E6B]"}`}>
            Annual <span className="ml-1 text-xs text-[#C2A46D]">save ~2 months</span>
          </button>
        </motion.div>
      </motion.section>

      <section className="px-6 pb-16 lg:px-12">
        <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-4">
          {ORDER.map((id, i) => {
            const p = planMap[id];
            if (!p) return null;
            const featured = id === "connectplus";
            return (
              <motion.div key={id} data-testid={`plan-card-${id}`}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.55, ease, delay: i * 0.1 }}
                className={`relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm ${featured ? "border-[#243447] shadow-xl lg:-translate-y-2" : "border-[#DDE2E7]"}`}>
                {featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#C2A46D] px-3 py-1 text-xs font-bold text-white">MOST POPULAR</span>}
                <h3 className="text-lg font-bold text-[#243447]">{p.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-[#243447]">${priceOf(p).toLocaleString()}</span>
                  <span className="text-sm text-[#8A93A0]">/{period === "monthly" ? "mo" : "yr"}</span>
                </div>
                <p className="mt-2 text-xs text-[#8A93A0]">+ ${p.impl.toLocaleString()} one-time implementation</p>
                <button onClick={() => openSignup(id)} data-testid={`plan-cta-${id}`}
                  className={`mt-5 flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${featured ? "bg-[#243447] text-white hover:bg-[#2E4056]" : "border border-[#DDE2E7] text-[#243447] hover:bg-[#E8EEF4]"}`}>
                  Get started <ArrowRight className="h-4 w-4" />
                </button>
                <ul className="mt-6 space-y-2.5">
                  {FEATURES[id].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#565E6B]">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#5F7FA3]" /> {f}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}

          <motion.div data-testid="plan-card-network"
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.55, ease, delay: 0.3 }}
            className="flex flex-col rounded-2xl border border-[#DDE2E7] bg-white p-6 shadow-sm">
            <h3 className="text-lg font-bold text-[#243447]">NETWORK</h3>
            <div className="mt-3 flex items-baseline gap-1"><span className="text-4xl font-bold text-[#243447]">$599+</span><span className="text-sm text-[#8A93A0]">/mo</span></div>
            <p className="mt-2 text-xs text-[#8A93A0]">Custom implementation from $2,500</p>
            <a href="mailto:hello@commconnect.app?subject=CommConnect%20NETWORK%20inquiry" data-testid="plan-cta-network"
              className="mt-5 flex items-center justify-center gap-2 rounded-full border border-[#DDE2E7] px-5 py-2.5 text-sm font-semibold text-[#243447] transition hover:bg-[#E8EEF4]">
              Contact us <ArrowRight className="h-4 w-4" />
            </a>
            <ul className="mt-6 space-y-2.5">
              {FEATURES.network.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-[#565E6B]"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#5F7FA3]" /> {f}</li>
              ))}
            </ul>
          </motion.div>
        </div>
        <motion.p variants={fadeUp} {...inView} className="mx-auto mt-8 max-w-2xl text-center text-xs text-[#8A93A0]">Implementation is a one-time service — we configure CommConnect around your governance, branding, roles, and routing before launch.</motion.p>
      </section>

      <Dialog open={!!dialogPlan} onOpenChange={(o) => !o && setDialogPlan(null)}>
        <DialogContent data-testid="signup-dialog" className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[#243447]">Get started with {dp?.name}</DialogTitle></DialogHeader>
          {dp && (
            <div className="space-y-4">
              <div className="rounded-lg border border-[#DDE2E7] bg-[#E8EEF4] p-3 text-sm">
                <div className="flex items-center justify-between"><span className="text-[#565E6B]">{period === "monthly" ? "Monthly" : "Annual"} subscription</span><span className="font-semibold text-[#243447]">${priceOf(dp).toLocaleString()}</span></div>
                <div className="flex items-center justify-between"><span className="text-[#565E6B]">One-time implementation</span><span className="font-semibold text-[#243447]">${dp.impl.toLocaleString()}</span></div>
                <div className="mt-1.5 flex items-center justify-between border-t border-[#DDE2E7] pt-1.5"><span className="font-medium text-[#243447]">Due today</span><span className="font-bold text-[#243447]">${(priceOf(dp) + dp.impl).toLocaleString()}</span></div>
              </div>
              <div className="space-y-3">
                <div><label className="text-sm font-medium text-[#243447]">Organization name *</label><input data-testid="signup-org" value={form.org_name} onChange={(e) => set("org_name", e.target.value)} className={inputCls} placeholder="e.g. Chi Omega Chapter" /></div>
                <div><label className="text-sm font-medium text-[#243447]">Your name *</label><input data-testid="signup-name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className={inputCls} /></div>
                <div><label className="text-sm font-medium text-[#243447]">Email *</label><input data-testid="signup-email" type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className={inputCls} /></div>
                <div><label className="text-sm font-medium text-[#243447]">Phone</label><input data-testid="signup-phone" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className={inputCls} /></div>
              </div>
              <button onClick={startCheckout} disabled={submitting} data-testid="signup-checkout-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#243447] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#2E4056] disabled:opacity-60">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue to secure checkout <ArrowRight className="h-4 w-4" /></>}
              </button>
              <p className="text-center text-xs text-[#8A93A0]">Payments are processed securely by Stripe.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
